import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import Calendar from '@databases/calendar/Calendar';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import {
  createField,
  getExistingFieldNames,
  getUniqueFieldName,
} from '@databases/utils/database/utils';
import { getDateField, ViewChangeTypes } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useFieldLoader from '@databases/views/useFieldLoader';
import useView from '@databases/views/useView';
import React, { FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import errorIllustration from '../../../../assets/generic_error_dark.svg';
import CompactEmptyState from '../../CompactEmptyState';
import { FIELD_TYPES } from '../../field-types/fieldTypes';
import EntryCardValue from '../entry-card/EntryCardValue';

export interface CalendarLayoutProps {
  canEditFields: boolean;
  canEditContent: boolean;
  contentId: string;
  renderSection: any;
  containerWidth: number;
}

const CalendarLayout: FunctionComponent<CalendarLayoutProps> = ({
  canEditFields,
  canEditContent,
  contentId,
}) => {
  const { view, setViewChange } = useView();
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();
  const [database, dateField] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const dateField = getDateField(database, view.settings.layout);

    return [database, dateField];
  });

  useFieldLoader({ contentId, fieldType: 'date' });

  const handleCreateDateField = useCallback(async () => {
    const fieldLabel = formatMessage(
      messages['field-settings.field-name-inline-edit-default-text'],
      {
        defaultFieldName: formatMessage(FIELD_TYPES[DatabaseFieldType.DATE].label),
      }
    );
    const fieldName = getUniqueFieldName(fieldLabel, getExistingFieldNames(database));
    const field = createField(DatabaseFieldType.DATE, {
      name: fieldName,
    });
    dispatch(
      addChangeToDraft(contentId, 'main', {
        type: DatabaseChangeType.ADD_FIELD,
        field,
        currentViewId: view.id,
      })
    );
    setViewChange({
      type: ViewChangeTypes.LAYOUT_DATE_FIELD_ID,
      value: field.id,
    });

    client.sendAddDateFieldEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
    });

    client.sendCreateDatabaseFieldEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: 'calendarLayout',
    });

    client.sendDatabaseFieldOptionUpdatedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: 'calendarLayout',
      isNewField: true,
      fieldSelection: DatabaseFieldType.DATE,
    });
  }, [client, contentId, database, dispatch, formatMessage, setViewChange, view.id]);

  if (!dateField) {
    return (
      <CompactEmptyState
        heading={formatMessage(messages['calendar-layout.empty-state-heading'])}
        description={formatMessage(messages['calendar-layout.empty-state-description'])}
        imageUrl={errorIllustration}
        buttonText={
          canEditFields
            ? formatMessage(messages['calendar-layout.empty-state-button-text'])
            : undefined
        }
        onButtonClick={canEditFields ? handleCreateDateField : undefined}
      />
    );
  }

  return (
    <Calendar canEditContent={canEditContent} EntryCardValueComponent={EntryCardValue} />
  );
};

export default CalendarLayout;
